import { supabase, auth } from './supabase';
import type { Folder } from '../types';
export type { Folder };

const FOLDER_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6',
];
let _colorIndex = 0;
export const pickDefaultColor = (_hint?: number): string => {
  const color = FOLDER_COLORS[_colorIndex % FOLDER_COLORS.length];
  _colorIndex++;
  return color;
};

// ── Row ↔ Folder mapping ──────────────────────────────────────────────────────

function rowToFolder(row: Record<string, unknown>): Folder {
  return {
    id: row.id as string,
    name: row.name as string,
    color: (row.color as string | undefined) ?? '#6366f1',
    createdAt: row.created_at as number,
  };
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

export const fetchFolders = async (): Promise<Folder[]> => {
  const user = auth.currentUser;
  if (!user) return [];
  const { data, error } = await supabase
    .from('folders')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(rowToFolder);
};

export const createFolder = async (name: string, color: string): Promise<Folder> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('folders')
    .insert({ user_id: user.id, name, color, created_at: Date.now() })
    .select()
    .single();
  if (error) throw error;
  return rowToFolder(data);
};

export const renameFolder = async (folderId: string, newName: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('folders')
    .update({ name: newName })
    .eq('id', folderId)
    .eq('user_id', user.id);
  if (error) throw error;
};

export const deleteFolder = async (folderId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  // Transcripts with this folder get folder_id set to NULL via FK ON DELETE SET NULL
  const { error } = await supabase
    .from('folders')
    .delete()
    .eq('id', folderId)
    .eq('user_id', user.id);
  if (error) throw error;
};

export const moveTranscriptToFolder = async (
  transcriptId: string,
  folderId: string | null,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('transcripts')
    .update({ folder_id: folderId })
    .eq('id', transcriptId)
    .eq('user_id', user.id);
  if (error) throw error;
};
